import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Troubleshoot session uploads and assignments | Paddiq';

export function TroubleshootSessionsPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Sessions troubleshooting</p>
            <h1>Fix session upload and assignment problems.</h1>
            <p className="guide-hero__lead">
              Match the symptom to the right fix, correct driver and vehicle details, and restore
              circuit-based laps without losing track of the original session.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/sessions')}
              >
                Open Sessions
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/upload-mychron-session"
              >
                View upload guide
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/troubleshoot-session-uploads" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#identify">Identify the symptom</a>
                </li>
                <li>
                  <a href="#upload">Fix an upload</a>
                </li>
                <li>
                  <a href="#assignment">Correct assignments</a>
                </li>
                <li>
                  <a href="#circuit">Restore circuit laps</a>
                </li>
                <li>
                  <a href="#confirm">Confirm the fix</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>Before you retry</strong>
                <span>Keep the original file and note the exact message Paddiq shows.</span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="identify">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">First check</p>
                  <h2>Identify what failed before changing the file.</h2>
                  <p>
                    Open the correct workspace and return to <strong>Sessions</strong>. Check
                    whether Upload is missing, the upload dialog shows an error, the run uploaded
                    with the wrong assignment, or the session opens without circuit-based laps.
                  </p>
                  <div className="guide-callout">
                    Do not re-export or upload a duplicate until you know whether the existing
                    session only needs its saved details corrected.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="upload">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Upload access and files</p>
                  <h2>Fix the access, type, or size issue.</h2>
                  <ol className="guide-instructions">
                    <li>
                      If the workspace is read-only, open <strong>Billing</strong> and follow the
                      subscription or partner-code guidance shown in Paddiq. Ask the workspace owner
                      if you cannot manage billing.
                    </li>
                    <li>In the MyChron importer, use JSON, XRZ, XRK, HRZ, HRK, or DRK.</li>
                    <li>Keep each MyChron upload at 100 MB or smaller.</li>
                    <li>
                      For GPX, select <strong>Uploading GPX, CSV, or another file?</strong>. GPX
                      provides GPS-based session data, not the native sensor channels in a MyChron
                      file.
                    </li>
                    <li>
                      Retry once and wait for the <strong>session is ready</strong> or{' '}
                      <strong>sessions are ready</strong> step.
                    </li>
                  </ol>
                  <p>
                    If Paddiq says a payment method is required, follow that message instead of
                    repeatedly retrying. A team member who cannot manage the billing account should
                    send the exact message to the workspace owner.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="assignment">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Driver and vehicle</p>
                  <h2>Correct an uploaded session in Assignments.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong>, expand its date and driver folders,
                      and select the affected session time to make it primary.
                    </li>
                    <li>
                      Expand <strong>Assignments</strong>.
                    </li>
                    <li>Choose the correct Driver and Vehicle.</li>
                    <li>
                      Select <strong>Save session details</strong>.
                    </li>
                  </ol>
                  <p>
                    Team owners, coaches, and mechanics can choose from team drivers. A driver is
                    limited to their own driver profile, and a parent is limited to linked drivers.
                    Ask a team administrator to review the roster links if the intended driver is
                    not available.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="circuit">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Lap timing</p>
                  <h2>Set up the saved circuit for circuit-based laps.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Session &amp; laps</strong> for the affected run.
                    </li>
                    <li>
                      Switch to <strong>Circuit Laps</strong> and open{' '}
                      <strong>Circuit setup</strong>.
                    </li>
                    <li>Choose the correct saved circuit or use the suggested match.</li>
                    <li>Confirm the circuit has a usable start/finish line.</li>
                    <li>Review the generated laps, then save the fixed laps.</li>
                  </ol>
                  <p>
                    If a saved circuit has no usable start/finish line, its geometry must be
                    completed before Paddiq can regenerate circuit laps. Switch to{' '}
                    <strong>Raw laps</strong> to inspect lap records already present in the source
                    file.
                  </p>
                  <p>
                    For the complete Match, Verify, Build laps, and Save workflow, read{' '}
                    <a className="guide-text-link" href="/resources/set-up-circuit-laps">
                      Set up circuit laps for a session
                    </a>
                    .
                  </p>
                </div>
              </section>

              <section className="guide-step" id="confirm">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Verification</p>
                  <h2>Confirm the session instead of assuming the retry worked.</h2>
                  <div className="guide-ready">
                    <h3>The issue is resolved when:</h3>
                    <ul>
                      <li>the intended run appears once in Sessions;</li>
                      <li>Assignments show the correct driver and vehicle;</li>
                      <li>the chosen circuit is saved when circuit timing is needed;</li>
                      <li>the expected raw or circuit laps are available; and</li>
                      <li>the map and source telemetry channels load without an error.</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Still blocked?</p>
                <h2>Send support the details that identify the failure</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Upload error</h3>
                    <p>
                      Include the exact message, file extension, file size, and whether you chose
                      the MyChron importer or the other-data link. Do not attach the telemetry file
                      unless support asks for it through an approved channel.
                    </p>
                  </article>
                  <article>
                    <h3>Missing assignment or laps</h3>
                    <p>
                      Include the session name, the intended driver and vehicle, and the circuit
                      name. State whether Raw laps work and whether Circuit setup reports a missing
                      start/finish line.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Ready to check the run?</p>
                  <h2>Return to Sessions and verify the result.</h2>
                  <p>Use the original session when its saved details are all that need fixing.</p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/sessions')}
                  >
                    Open Sessions
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
